import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Share, Plus, X, Home, Sparkles, Bell, Smartphone, Monitor } from "lucide-react";

const DISMISS_KEY = "familjen_install_modal_dismissed_at";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 dagar
const ICON_URL = "/apple-touch-icon-180.png";

// Modalen visas BARA på dashboarden — inte i admin-vyer (settings, health) eller på login.
const ALLOWED_PATHS = new Set(["/"]);

const detectPlatform = () => {
  if (typeof window === "undefined") return "unknown";
  const ua = window.navigator.userAgent;
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  if (standalone) return "installed";
  if (/iPhone|iPad|iPod/.test(ua) && !window.MSStream) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
};

/**
 * InstallModal — STOR välkomstmodal som visas på första sidan när användaren INTE
 * är i PWA-läge. Förklarar varför + hur man installerar, plattformsspecifikt.
 *
 * Inställningar:
 * - Visas EN gång per 7-dagars cooldown (lagras i localStorage).
 * - Visas inte om window.navigator.standalone === true (redan installerat).
 * - Animerad slide-in från botten.
 */
export function InstallModal() {
  const location = useLocation();
  const [platform, setPlatform] = useState("unknown");
  const [open, setOpen] = useState(false);
  const [deferredEvt, setDeferredEvt] = useState(null);

  useEffect(() => {
    // Modalen är reserverad för dashboarden — annars stör den admin-flow
    if (!ALLOWED_PATHS.has(location.pathname)) {
      setOpen(false);
      return undefined;
    }
    const p = detectPlatform();
    setPlatform(p);
    if (p === "installed" || p === "unknown") return undefined;

    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return undefined;

    // Liten delay så modalen inte poppar upp instant
    const t = setTimeout(() => setOpen(true), 800);

    const onBefore = (e) => {
      e.preventDefault();
      setDeferredEvt(e);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    return () => {
      clearTimeout(t);
      window.removeEventListener("beforeinstallprompt", onBefore);
    };
  }, [location.pathname]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setOpen(false);
  };

  const tryNativeInstall = async () => {
    if (deferredEvt) {
      try {
        deferredEvt.prompt();
        const { outcome } = await deferredEvt.userChoice;
        if (outcome === "accepted") dismiss();
      } catch (_) { /* ignore */ }
    }
  };

  if (!open) return null;

  // Plattformsspecifika steg
  const steps = platform === "ios" ? [
    {
      n: 1,
      icon: <Share size={16} />,
      text: <>Tryck på <span className="font-semibold">Dela</span>-knappen <Share size={13} className="inline -mt-0.5" /> längst ner i Safari.</>,
    },
    {
      n: 2,
      icon: <Plus size={16} />,
      text: <>Skrolla ner i menyn och välj <span className="font-semibold">"Lägg till på hemskärmen"</span>.</>,
    },
    {
      n: 3,
      icon: <Home size={16} />,
      text: <>Tryck <span className="font-semibold">Lägg till</span> uppe i högra hörnet. Öppna sedan ikonen "Familjen" från hemskärmen.</>,
    },
  ] : platform === "android" ? [
    {
      n: 1,
      icon: <Plus size={16} />,
      text: <>Tryck på <span className="font-semibold">⋮ menyknappen</span> uppe till höger i Chrome.</>,
    },
    {
      n: 2,
      icon: <Home size={16} />,
      text: <>Välj <span className="font-semibold">"Installera appen"</span> eller <span className="font-semibold">"Lägg till på startskärmen"</span>.</>,
    },
    {
      n: 3,
      icon: <Sparkles size={16} />,
      text: <>Bekräfta och öppna sedan "Familjen" från hemskärmen.</>,
    },
  ] : [
    {
      n: 1,
      icon: <Plus size={16} />,
      text: <>Klicka på <span className="font-semibold">install-ikonen</span> i adressfältet (ser ut som en skärm med en pil).</>,
    },
    {
      n: 2,
      icon: <Sparkles size={16} />,
      text: <>Bekräfta installationen i dialogrutan som dyker upp.</>,
    },
    {
      n: 3,
      icon: <Monitor size={16} />,
      text: <>Familjen öppnas nu som en egen app från startmenyn eller skrivbordet.</>,
    },
  ];

  const platformLabel = platform === "ios" ? "iPhone/iPad" : platform === "android" ? "Android" : "datorn";
  const PlatformIcon = platform === "desktop" ? Monitor : Smartphone;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-6 animate-in fade-in duration-300"
      data-testid="install-modal-backdrop"
      onClick={dismiss}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-500"
        data-testid="install-modal"
      >
        {/* Header med ikon */}
        <div className="bg-gradient-to-br from-[color:var(--moss)] to-[#1f421f] px-6 pt-8 pb-6 text-white relative">
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            data-testid="install-modal-close"
            aria-label="Stäng"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-4">
            <img
              src={ICON_URL}
              alt="Familjen"
              className="w-16 h-16 rounded-2xl shadow-lg ring-2 ring-white/20"
            />
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] opacity-80 flex items-center gap-1">
                <PlatformIcon size={10} /> Installera på {platformLabel}
              </p>
              <h2 className="font-display text-2xl font-medium mt-1 leading-tight">
                Lägg till Familjen på hemskärmen
              </h2>
            </div>
          </div>
        </div>

        {/* Why */}
        <div className="px-6 pt-5 pb-3">
          <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
            Familjen fungerar bäst som en <span className="font-semibold text-[color:var(--text-primary)]">installerad app</span>. Då får ni:
          </p>
          <div className="mt-3 space-y-2">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#EAF3EA] text-[color:var(--moss)] flex items-center justify-center flex-shrink-0">
                <Bell size={15} />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm font-medium">Push-notiser</p>
                <p className="text-xs text-[color:var(--text-tertiary)]">Påminnelser om läxor, prov och söndagsbriefingen — kräver installation på iOS.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#FFF7E6] text-[#A86A1A] flex items-center justify-center flex-shrink-0">
                <Sparkles size={15} />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm font-medium">Full skärm</p>
                <p className="text-xs text-[color:var(--text-tertiary)]">Inget adressfält, inga distraktioner — bara familjens portal.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#EEF2F7] text-[color:var(--jarvis)] flex items-center justify-center flex-shrink-0">
                <Home size={15} />
              </div>
              <div className="flex-1 pt-1">
                <p className="text-sm font-medium">Snabbåtkomst</p>
                <p className="text-xs text-[color:var(--text-tertiary)]">Ett tryck på hemskärmen istället för att leta upp länken.</p>
              </div>
            </div>
          </div>
        </div>

        {/* How */}
        <div className="px-6 pt-2 pb-5">
          <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[color:var(--moss)] mb-3">
            Så här installerar du
          </p>
          <ol className="space-y-3">
            {steps.map((s) => (
              <li key={s.n} className="flex items-start gap-3" data-testid={`install-step-${s.n}`}>
                <span className="w-7 h-7 rounded-full bg-[color:var(--moss)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {s.n}
                </span>
                <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed pt-0.5">
                  {s.text}
                </p>
              </li>
            ))}
          </ol>
        </div>

        {/* Actions */}
        <div className="bg-[#F9F7F2] px-6 py-4 flex gap-3 border-t border-[#E5E7EB]">
          {platform === "android" && deferredEvt ? (
            <button
              onClick={tryNativeInstall}
              className="flex-1 h-11 rounded-xl bg-[color:var(--moss)] text-white text-sm font-medium hover:bg-[#1f421f] transition-colors"
              data-testid="install-modal-install-button"
            >
              Installera nu
            </button>
          ) : (
            <button
              onClick={dismiss}
              className="flex-1 h-11 rounded-xl bg-[color:var(--moss)] text-white text-sm font-medium hover:bg-[#1f421f] transition-colors"
              data-testid="install-modal-done"
            >
              Jag fixar det
            </button>
          )}
          <button
            onClick={dismiss}
            className="px-4 h-11 rounded-xl bg-transparent text-[color:var(--text-tertiary)] text-sm font-medium hover:bg-white/50 transition-colors"
            data-testid="install-modal-later"
          >
            Senare
          </button>
        </div>
      </div>
    </div>
  );
}

export default InstallModal;
